/**
 * Phase E4 (K5): the taste lift, read time only.
 *
 * `taste_signal` has carried denormalised string features since WP 2.4 and
 * nothing read them — this file, and the `taste_affinity` view it reads, are
 * the first reader. `score` stays the profile's judgement of the event;
 * `affinityLift` is Doug's own history with the people and ensembles in it,
 * a small bounded number added on top at render time. Nothing here re-scores
 * anything, and nothing here writes a sentence (H2): the only visible effect
 * of a lift is where a row sits in an already-sorted list.
 *
 * No imports, the same rule `rowMeta.ts` and `today.ts` live by:
 * `test/affinity.test.ts` loads this file directly from the Worker's suite.
 * `browse.ts`, `browseRank.ts` and `today.ts` are the same kind of file, so
 * none of them can import this one either — each restates the tiny formula
 * below with its own copy of these three constants, guarded by a cross-check
 * test against this file, the same arrangement `browseRank` itself already
 * has between `browse.ts` and `browseRank.ts`.
 *
 * The one import is `rankBase.ts`, itself dependency-free: the unscored
 * default is a decision shared with those files, not a number to restate.
 */
import { UNSCORED_RANK } from './rankBase.ts'

/** Per positive unit of weight, summed over the row's distinct entities. */
export const PER_UNIT = 0.03

/** The positive side's ceiling, before the dismiss penalty is applied. */
export const AFFINITY_CAP = 0.10

/** Once per entry, when any one entity on it has been dismissed twice or
 *  more (rule 4: "dismissed twice, not once" — one dismissal is noise). */
export const DISMISS_PENALTY = 0.05

export interface AffinityRow {
  kind: 'person' | 'ensemble'
  id: string
  positive: number
  negative: number
}

/** Keyed `${kind}:${id}` — one entry per person or ensemble, read off
 *  `taste_affinity`. */
export type AffinityMap = Map<string, AffinityRow>

export function affinityKey(kind: string, id: string): string {
  return `${kind}:${id}`
}

/** The shape `affinityLift`/`effectiveScore`/`idsOf` read off a row — the
 *  `{id, role}` pairs `feed_lite`'s `people`/`ensembles` columns carry. */
interface RowEntities {
  people?: { id: string }[] | null
  ensembles?: { id: string }[] | null
}

/**
 * Sum of positive weight over the row's distinct entities (rule 2: a
 * conductor who is also the programme's composer is one entity, not two),
 * capped at `AFFINITY_CAP`, minus `DISMISS_PENALTY` once if any of them has
 * been dismissed twice or more. Clamped to `[-DISMISS_PENALTY, AFFINITY_CAP]`.
 * `0` for an empty map or a row that names no one.
 */
export function affinityLift(row: RowEntities, map: AffinityMap): number {
  const seen = new Set<string>()
  let positive = 0
  let dismissedTwice = false

  const entities: { kind: 'person' | 'ensemble'; id: string }[] = [
    ...(row.people ?? []).map(p => ({ kind: 'person' as const, id: p.id })),
    ...(row.ensembles ?? []).map(s => ({ kind: 'ensemble' as const, id: s.id })),
  ]

  for (const { kind, id } of entities) {
    const key = affinityKey(kind, id)
    if (seen.has(key)) continue
    seen.add(key)
    const a = map.get(key)
    if (!a) continue
    positive += a.positive
    if (a.negative >= 2) dismissedTwice = true
  }

  let lift = Math.min(positive * PER_UNIT, AFFINITY_CAP)
  if (dismissedTwice) lift -= DISMISS_PENALTY
  return Math.max(-DISMISS_PENALTY, Math.min(AFFINITY_CAP, lift))
}

/** The model's score, lifted — the one number the four ranked lists sort
 *  on. `fallback` is the unscored default (`UNSCORED_RANK`, `rankBase.ts`). */
export function effectiveScore(
  row: { score?: number | null } & RowEntities,
  map: AffinityMap,
  fallback = UNSCORED_RANK,
): number {
  return (row.score ?? fallback) + affinityLift(row, map)
}

/** What `signalFeatures` spreads onto a written signal — `[]`, never
 *  `undefined`, for a row that names no one. */
export function idsOf(row: RowEntities): { person_ids: string[]; ensemble_ids: string[] } {
  return {
    person_ids: (row.people ?? []).map(p => p.id),
    ensemble_ids: (row.ensembles ?? []).map(s => s.id),
  }
}
